// ReinstatementRequestsList.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CardContent,
    Grid,
    TextField,
    InputAdornment,
    Skeleton,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosServices from 'utils/axios';
import useAuth from 'hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Page from 'ui-component/Page';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import MainCard from './MainCard';
import { EmployeeActions } from 'store/slices/employees';
import { useDispatch } from 'react-redux';

export default function ReinstatementRequestsList() {
    const { user } = useAuth();
    const isAdmin = user?.roleName === 'ADMIN';
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loadRequestsCount } = EmployeeActions;

    // table state
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // dialogs & menus
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isWorking, setIsWorking] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    // redirect non-admins
    useEffect(() => {
        if (user && !isAdmin) navigate('/listing/manage-listing');
    }, [user, isAdmin, navigate]);

    // fetcher
    const fetchRequests = useCallback(async () => {
        if (!isAdmin) return;
        setLoading(true);
        try {
            const { data: res } = await axiosServices.get('/user/reinstatement-requests', {
                headers: { Authorization: `Bearer ${localStorage.getItem('serviceToken')}` },
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                    search: search || undefined
                }
            });
            setData(res.requests);
            setTotalCount(res.totalRequests);
        } catch (err) {
            console.error('Failed to load reinstatement requests', err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, isAdmin]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // handlers
    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };
    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setPage(0);
    };

    const handleMenuOpen = (e, row) => {
        setAnchorEl(e.currentTarget);
        setMenuRow(row);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRow(null);
    };

    const handleConfirm = async () => {
        setIsWorking(true);
        try {
            await axiosServices.patch(
                '/user/reinstate-policy-admin',
                { user_id: selectedUserId },
                { headers: { Authorization: `Bearer ${localStorage.getItem('serviceToken')}` } }
            );
            await fetchRequests();
            dispatch(loadRequestsCount());
        } catch (err) {
            console.error(err);
        } finally {
            setIsWorking(false);
            setDialogOpen(false);
        }
    };

    // client-side status filtering
    const filteredData = data.filter((row) => {
        const isRequested = !!row.reinstatementRequestedAt;
        const isReinstated = row.status === 'VERIFIED' && !row.reinstatementRequestedAt;
        if (statusFilter === 'pending') return isRequested;
        if (statusFilter === 'approved') return isReinstated;
        return true;
    });

    return (
        <Page title="Dashboard: Reinstatement Requests">
            <Breadcrumbs heading="Clients" links={[{ name: 'Reinstatement Requests', href: '/reinstatement-requests' }]} />
            <MainCard title="Reinstatement Requests" content={false}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={search}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select label="Status" value={statusFilter} onChange={handleStatusFilter}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Requested At</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? [...Array(rowsPerPage)].map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={5}>
                                              <Skeleton variant="rectangular" height={32} />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : filteredData.map((row) => (
                                      <TableRow key={row.id}>
                                          <TableCell>{`${row.firstName || ''} ${row.lastName || ''}`}</TableCell>
                                          <TableCell>{row.email}</TableCell>
                                          <TableCell>{new Date(row.reinstatementRequestedAt).toLocaleDateString()}</TableCell>
                                          <TableCell>
                                              <Chip
                                                  label={
                                                      row.status === 'VERIFIED' && !row.reinstatementRequestedAt ? 'Approved' : 'Pending'
                                                  }
                                                  color={row.status === 'VERIFIED' && !row.reinstatementRequestedAt ? 'success' : 'warning'}
                                                  size="small"
                                                  sx={{ color: 'white' }}
                                              />
                                          </TableCell>
                                          <TableCell align="center">
                                              <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                                                  <MoreVertIcon fontSize="small" />
                                              </IconButton>
                                          </TableCell>
                                      </TableRow>
                                  ))}
                            {!loading && filteredData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="h4" sx={{ p: 2 }}>
                                            No requests found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box display="flex" justifyContent="flex-end" p={1}>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </Box>

                {/* action menu */}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <MenuItem
                        disabled={menuRow?.status === 'VERIFIED'}
                        onClick={() => {
                            setSelectedUserId(menuRow.id);
                            setDialogOpen(true);
                            handleMenuClose();
                        }}
                    >
                        <CheckCircleIcon color="success" sx={{ fontSize: '1.2rem', mr: 1 }} />
                        Reinstate
                    </MenuItem>
                </Menu>

                {/* confirm dialog */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                    <DialogTitle>Confirm Reinstate</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Reinstating services will reactivate this user’s account and all associated data.
                            <strong> Proceed?</strong>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton loading={isWorking} color="success" onClick={handleConfirm} autoFocus>
                            Yes, Reinstate
                        </LoadingButton>
                        <Button color="error" onClick={() => setDialogOpen(false)}>
                            No, Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
            </MainCard>
        </Page>
    );
}
